import React from 'react';
import { Container, Text } from '@charlietango/ui';
import RichText from '../RichText/RichText';
import { FontWeight, TextVariant } from '../../styles/typography';
import { colors } from '../../styles/colors';
import { rem } from '../../styles/rem';

export type AlertBannerProps = {
  message?: string;
  icon?: React.ReactNode;
  label?: React.ReactNode;
  variant?: 'alert' | 'status';
  sx?: Record<string, any>;
};

function AlertBanner({
  message,
  icon,
  label,
  variant = 'alert',
  sx,
}: AlertBannerProps) {
  if (!message) return null;

  const palette =
    variant === 'status'
      ? colors.components.notificationBar
      : colors.components.alertBanner;

  return (
    <div
      role="alert"
      sx={{
        width: '100%',
        minHeight: rem(36),
        color: palette.Text.Default,
        backgroundColor: palette.Background.Default,
        py: rem(6),
        ...sx,
      }}
      // Add nosnippet to prevent Google from indexing
      // https://developers.google.com/search/docs/advanced/crawling/special-tags#inline-directives
      data-nosnippet=""
    >
      <Container
        sx={{
          maxWidth: ['none', 'none', rem(952)],
          px: [rem(20), rem(20), 0],
        }}
      >
        <div
          sx={{
            display: 'flex',
            flexDirection: ['column', 'row'],
            alignItems: ['flex-start', 'center'],
            columnGap: rem(16),
            rowGap: rem(4),
          }}
        >
          {(icon || label) && (
            <div
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: rem(8),
                flex: '0 0 auto',
              }}
            >
              {icon && (
                <div
                  sx={{
                    width: rem(20),
                    height: rem(20),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                    color: palette.Icon.Default,
                  }}
                >
                  {icon}
                </div>
              )}
              {label && (
                <Text
                  as="span"
                  variant={TextVariant.BodyMd}
                  sx={{
                    color: palette.Text.Default,
                    fontWeight: FontWeight.Medium,
                    lineHeight: rem(24),
                  }}
                >
                  {label}
                </Text>
              )}
            </div>
          )}
          <div
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              maxWidth: '80ch',
              pl: [icon && label ? rem(28) : 0, 0],
            }}
          >
            <RichText variant={TextVariant.BodyMd}>{message}</RichText>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default AlertBanner;
